/**
 * Where the four values the bundle needs come from — and which source answered.
 *
 * The install is **one line** in `layout/theme.liquid` (`docs/STOREFRONT.md` §1):
 *
 *     <script src="https://peakcheckout.com/peak-checkout.js" data-peak-key="peak_pk_…" defer></script>
 *
 * Only the public key is written by hand. Everything else is derived, in this order:
 *
 * | Value | Order | Sources |
 * | --- | --- | --- |
 * | `serviceUrl` | attribute → script `src` | `data-peak-service`, else the origin of the tag's own `src` |
 * | `shopDomain` | Shopify global → attribute → hostname | `window.Shopify.shop`, else `data-peak-shop`, else `location.hostname` |
 * | `cartUrl` | attribute → Shopify global → default | `data-peak-cart-url`, else `window.Shopify.routes.root` + `cart.js`, else `/cart.js` |
 *
 * `shopDomain` is the one value where the global outranks the attribute, on purpose:
 * `window.Shopify.shop` is the shop's permanent `*.myshopify.com` domain, set by Shopify, and
 * it is exactly what `POST /api/checkout/create` matches the tenant's connection against
 * (`src/lib/checkout/create/create-checkout.ts`, check 5). A theme setting or a pasted
 * attribute can be stale; the global cannot.
 *
 * Both spellings of every attribute are read. A theme installed before the rename to the `peak`
 * prefix (`DECISIONS.md` D105) carries the old snippet: it writes `data-yc-*` and loads
 * `/yc-checkout.js`, a path `next.config.ts` rewrites onto this bundle. The caller warns once
 * when the old spelling is what answered.
 *
 * Pure and DOM-free (it takes {@link PeakScriptLike} and a plain {@link PeakBootstrapEnvironment},
 * not an `Element` and not `window`) so every rule above is unit-testable without a browser:
 * `tests/unit/storefront/bootstrap.test.ts`.
 */

import {
  cartUrlFromRoutesRoot,
  PEAK_DEFAULT_CART_URL,
  resolveShopDomain,
  shopifyRoutesRoot,
  shopifyShopDomain,
} from './cart';
import { normaliseServiceUrl, originOfScriptSrc } from './config';

/** Current spelling first, the pre-rename one second. Order is the precedence. */
export const PEAK_SCRIPT_ATTRIBUTES = {
  key: ['data-peak-key', 'data-yc-key'],
  service: ['data-peak-service', 'data-yc-service'],
  shop: ['data-peak-shop', 'data-yc-shop'],
  cartUrl: ['data-peak-cart-url', 'data-yc-cart-url'],
} as const;

/** The attributes that identify our own tag, for `document.querySelector`. */
export const PEAK_SCRIPT_SELECTOR = PEAK_SCRIPT_ATTRIBUTES.key
  .map((name) => `script[${name}]`)
  .join(', ');

/** All this module needs of a `<script>`, so a plain object serves in tests. */
export type PeakScriptLike = { getAttribute: (name: string) => string | null };

/**
 * Which of the four possible sources produced a value. Logged once per page load as a single
 * `console.debug` line, so a support screenshot of the console answers "where did that come
 * from?" without a theme export.
 */
export type PeakValueSource =
  /** An explicit `data-peak-*` (or pre-rename `data-yc-*`) attribute on the tag. */
  | 'attribute'
  /** The origin of the tag's own `src` — the one-line install. */
  | 'script-src'
  /** `window.Shopify` (`shop`, or `routes.root`). */
  | 'shopify-global'
  /** Nothing else answered: the page hostname, or `/cart.js`. */
  | 'default';

/** Where each derived value came from. `publicKey` is always an attribute, so it is not here. */
export type PeakBootstrapSources = {
  service: PeakValueSource;
  shop: PeakValueSource;
  cartUrl: PeakValueSource;
};

/** Everything the resolution reads from outside the tag itself. */
export type PeakBootstrapEnvironment = {
  /**
   * The tag's own `src`, **absolute** — in the DOM that is `script.src`, which the browser has
   * already resolved. `null` when there is none, or when it could not be read (a tag the theme
   * assembled without a `src`, a `currentScript` a tag manager hid).
   */
  scriptSrc: string | null;
  /** `window.Shopify`, whatever it turns out to be. `undefined` off Shopify. */
  shopifyGlobal: unknown;
  /** `window.location.hostname`, the last resort for the shop domain. */
  hostname: string;
};

export type PeakBootstrap = {
  publicKey: string;
  serviceUrl: string;
  shopDomain: string;
  cartUrl: string;
  /** True when any value came from a pre-rename `data-yc-*` attribute. */
  legacyAttributes: boolean;
  sources: PeakBootstrapSources;
};

/** True when this element carries a key attribute in either spelling. */
export function hasKeyAttribute(script: PeakScriptLike): boolean {
  return PEAK_SCRIPT_ATTRIBUTES.key.some((name) => script.getAttribute(name) !== null);
}

/** The first spelling that is present at all, trimmed; `legacy` is true past the first name. */
function read(
  script: PeakScriptLike,
  names: readonly string[],
): { value: string; legacy: boolean } {
  for (const [index, name] of names.entries()) {
    const raw = script.getAttribute(name);
    if (raw !== null) return { value: raw.trim(), legacy: index > 0 };
  }
  return { value: '', legacy: false };
}

/**
 * The tag's configuration, or `null` when it is not usable — no key, or no service origin from
 * either the attribute or the tag's `src`. The caller then attaches nothing and the theme
 * behaves exactly as Shopify shipped it.
 */
export function resolveBootstrap(
  script: PeakScriptLike,
  environment: PeakBootstrapEnvironment,
): PeakBootstrap | null {
  const key = read(script, PEAK_SCRIPT_ATTRIBUTES.key);
  if (key.value === '') return null;

  // --- service ------------------------------------------------------------
  // The attribute is the override (a staging service, a tag manager that rewrote the `src`);
  // the tag's own origin is the default, and it is the whole point of the one-line install.
  const serviceAttribute = read(script, PEAK_SCRIPT_ATTRIBUTES.service);
  const serviceFromAttribute = normaliseServiceUrl(serviceAttribute.value);
  const serviceFromScriptSrc = originOfScriptSrc(environment.scriptSrc);

  const usesServiceAttribute = serviceFromAttribute !== '';
  const serviceUrl = usesServiceAttribute ? serviceFromAttribute : serviceFromScriptSrc;
  if (serviceUrl === '') return null;

  // --- shop ---------------------------------------------------------------
  const shop = read(script, PEAK_SCRIPT_ATTRIBUTES.shop);
  const shopAttribute = shop.value === '' ? null : shop.value;
  const shopFromGlobal = shopifyShopDomain(environment.shopifyGlobal);

  // --- cart ---------------------------------------------------------------
  const cart = read(script, PEAK_SCRIPT_ATTRIBUTES.cartUrl);
  const routesRoot = shopifyRoutesRoot(environment.shopifyGlobal);
  const cartUrl =
    cart.value !== ''
      ? cart.value
      : routesRoot === null
        ? PEAK_DEFAULT_CART_URL
        : cartUrlFromRoutesRoot(routesRoot);

  return {
    publicKey: key.value,
    serviceUrl,
    shopDomain: resolveShopDomain(environment.shopifyGlobal, shopAttribute, environment.hostname),
    cartUrl,
    // Only an attribute that actually answered can be legacy: a `data-yc-service` on a tag whose
    // service came from the `src` is not what the warning is about.
    legacyAttributes:
      key.legacy ||
      (usesServiceAttribute && serviceAttribute.legacy) ||
      (shopFromGlobal === null && shopAttribute !== null && shop.legacy) ||
      (cart.value !== '' && cart.legacy),
    sources: {
      service: usesServiceAttribute ? 'attribute' : 'script-src',
      shop:
        shopFromGlobal !== null
          ? 'shopify-global'
          : shopAttribute !== null
            ? 'attribute'
            : 'default',
      cartUrl: cart.value !== '' ? 'attribute' : routesRoot !== null ? 'shopify-global' : 'default',
    },
  };
}

/** One line for the console: which source each derived value came from, and what it produced. */
export function describeBootstrap(bootstrap: PeakBootstrap): string {
  return (
    'service=' +
    bootstrap.serviceUrl +
    ' (' +
    bootstrap.sources.service +
    ') shop=' +
    bootstrap.shopDomain +
    ' (' +
    bootstrap.sources.shop +
    ') cart=' +
    bootstrap.cartUrl +
    ' (' +
    bootstrap.sources.cartUrl +
    ')'
  );
}
